const BASE_URL = 'https://raider.io/api/v1'

export const DYNAMIC_DATA_TTL_MS = 30 * 60 * 1000
export const STATIC_DATA_TTL_MS = 24 * 60 * 60 * 1000

const MAX_REQUEST_CACHE_ENTRIES = 500
const MAX_PERSISTED_CACHE_ENTRIES = 100
const MAX_PERSISTED_CACHE_ENTRY_LENGTH = 1_500_000
const PERSISTED_CACHE_PREFIX = 'rio-raider-api-cache-v1:'
const BROWSER_CACHE_NAME = 'rio-raider-api-data-v1'
const requestCache = new Map()

function getSessionStorage() {
  try {
    return typeof sessionStorage === 'undefined' ? null : sessionStorage
  } catch {
    return null
  }
}

function hashCacheKey(value) {
  let hash = 2166136261
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index)
    hash = Math.imul(hash, 16777619)
  }
  return (hash >>> 0).toString(36)
}

function persistedCacheKey(url) {
  return `${PERSISTED_CACHE_PREFIX}${hashCacheKey(url)}`
}

function persistedCacheEntries(storage) {
  const entries = []
  for (let index = 0; index < storage.length; index += 1) {
    const key = storage.key(index)
    if (!key?.startsWith(PERSISTED_CACHE_PREFIX)) continue
    try {
      const value = JSON.parse(storage.getItem(key) || 'null')
      entries.push({ key, updatedAt: Number(value?.updatedAt || 0) })
    } catch {
      storage.removeItem(key)
      index -= 1
    }
  }
  return entries
}

function trimPersistedCache(storage, targetSize = MAX_PERSISTED_CACHE_ENTRIES) {
  const entries = persistedCacheEntries(storage)
    .sort((left, right) => left.updatedAt - right.updatedAt)
  while (entries.length > targetSize) {
    storage.removeItem(entries.shift().key)
  }
}

function loadPersistedCacheEntry(url, ttl, now) {
  const storage = getSessionStorage()
  if (!storage) return null

  const key = persistedCacheKey(url)
  try {
    const value = JSON.parse(storage.getItem(key) || 'null')
    const updatedAt = Number(value?.updatedAt || 0)
    if (value?.url !== url || value.data === undefined || !updatedAt || now - updatedAt >= ttl) {
      if (value) storage.removeItem(key)
      return null
    }
    return {
      data: value.data,
      updatedAt,
      lastAccessedAt: now,
      promise: null,
      requestVersion: 0,
    }
  } catch {
    storage.removeItem(key)
    return null
  }
}

function persistCacheEntry(url, entry) {
  const storage = getSessionStorage()
  if (!storage || entry.data === undefined || !entry.updatedAt) return

  const key = persistedCacheKey(url)
  const value = JSON.stringify({
    url,
    data: entry.data,
    updatedAt: entry.updatedAt,
  })
  if (value.length > MAX_PERSISTED_CACHE_ENTRY_LENGTH) return

  try {
    storage.setItem(key, value)
    trimPersistedCache(storage)
  } catch {
    const removableEntries = persistedCacheEntries(storage)
      .filter((item) => item.key !== key)
      .sort((left, right) => left.updatedAt - right.updatedAt)
    while (removableEntries.length) {
      storage.removeItem(removableEntries.shift().key)
      try {
        storage.setItem(key, value)
        trimPersistedCache(storage)
        return
      } catch {
        // Keep evicting the oldest entries until this response fits.
      }
    }
    // Large responses remain available in memory when session storage is full.
  }
}

function clearPersistedCache() {
  const storage = getSessionStorage()
  if (!storage) return
  persistedCacheEntries(storage).forEach(({ key }) => storage.removeItem(key))
}

function getBrowserCacheStorage() {
  try {
    return typeof caches === 'undefined' ? null : caches
  } catch {
    return null
  }
}

async function loadBrowserCacheEntry(url, ttl, now) {
  const cacheStorage = getBrowserCacheStorage()
  if (!cacheStorage) return null

  try {
    const cache = await cacheStorage.open(BROWSER_CACHE_NAME)
    const response = await cache.match(url)
    if (!response) return null

    const value = await response.json()
    const updatedAt = Number(value?.updatedAt || 0)
    if (value?.url !== url || value.data === undefined || !updatedAt || now - updatedAt >= ttl) {
      await cache.delete(url)
      return null
    }
    return {
      data: value.data,
      updatedAt,
      lastAccessedAt: now,
      promise: null,
      requestVersion: 0,
    }
  } catch {
    return null
  }
}

async function persistBrowserCacheEntry(url, entry) {
  const cacheStorage = getBrowserCacheStorage()
  if (!cacheStorage || entry.data === undefined || !entry.updatedAt) return

  try {
    const cache = await cacheStorage.open(BROWSER_CACHE_NAME)
    await cache.put(url, new Response(JSON.stringify({
      url,
      data: entry.data,
      updatedAt: entry.updatedAt,
    }), {
      headers: { 'Content-Type': 'application/json' },
    }))
  } catch {
    // In-memory and session caches remain available if Cache Storage is unavailable.
  }
}

function clearBrowserCache() {
  const cacheStorage = getBrowserCacheStorage()
  if (cacheStorage) cacheStorage.delete(BROWSER_CACHE_NAME).catch(() => {})
}

function createUrl(path, params = {}) {
  const url = new URL(`${BASE_URL}${path}`)

  Object.entries(params)
    .sort(([left], [right]) => left.localeCompare(right))
    .forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        url.searchParams.set(key, value)
      }
    })

  return url.toString()
}

function trimRequestCache() {
  if (requestCache.size <= MAX_REQUEST_CACHE_ENTRIES) return

  const removableEntries = [...requestCache.entries()]
    .filter(([, entry]) => !entry.promise)
    .sort(([, left], [, right]) => left.lastAccessedAt - right.lastAccessedAt)

  while (requestCache.size > MAX_REQUEST_CACHE_ENTRIES && removableEntries.length) {
    const [key] = removableEntries.shift()
    requestCache.delete(key)
  }
}

function noteCacheAccess(options, updatedAt) {
  const cacheContext = options.cacheContext
  if (!cacheContext || !updatedAt) return

  cacheContext.oldestUpdatedAt = cacheContext.oldestUpdatedAt
    ? Math.min(cacheContext.oldestUpdatedAt, updatedAt)
    : updatedAt
}

function noteNetworkRequest(options) {
  if (options.cacheContext) options.cacheContext.networkRequested = true
}

async function request(path, params, options = {}) {
  const url = createUrl(path, params)
  const cacheEnabled = options.cache !== false
  const force = options.force === true
  const ttl = Number(options.ttl || DYNAMIC_DATA_TTL_MS)
  const now = Date.now()
  let cached = cacheEnabled ? requestCache.get(url) : null
  if (cacheEnabled && !cached) {
    cached = loadPersistedCacheEntry(url, ttl, now)
      || await loadBrowserCacheEntry(url, ttl, now)
    if (cached) requestCache.set(url, cached)
  }

  if (!force && cached?.promise) {
    cached.lastAccessedAt = now
    return cached.promise.then((data) => {
      noteCacheAccess(options, cached.updatedAt)
      return data
    })
  }

  if (!force && cached?.data !== undefined && now - cached.updatedAt < ttl) {
    cached.lastAccessedAt = now
    noteCacheAccess(options, cached.updatedAt)
    return cached.data
  }

  noteNetworkRequest(options)
  const requestPromise = fetch(url, { cache: 'no-store' })
    .then(async (response) => {
      if (!response.ok) {
        const responseText = await response.text()
        const error = new Error(responseText || `Raider.io request failed: ${response.status}`)
        error.status = response.status
        throw error
      }

      return response.json()
    })

  if (!cacheEnabled) {
    return requestPromise
  }

  const entry = cached || {
    data: undefined,
    updatedAt: 0,
    lastAccessedAt: now,
    promise: null,
    requestVersion: 0,
  }

  const requestVersion = Number(entry.requestVersion || 0) + 1
  entry.requestVersion = requestVersion
  const trackedPromise = requestPromise
    .then(async (data) => {
      const completedAt = Date.now()
      if (entry.requestVersion === requestVersion) {
        entry.data = data
        entry.updatedAt = completedAt
        entry.lastAccessedAt = completedAt
        persistCacheEntry(url, entry)
        await persistBrowserCacheEntry(url, entry)
      }
      noteCacheAccess(options, completedAt)
      return data
    })
    .finally(() => {
      if (entry.requestVersion === requestVersion) {
        entry.promise = null
      }
      trimRequestCache()
    })

  entry.promise = trackedPromise
  requestCache.set(url, entry)
  trimRequestCache()
  return trackedPromise
}

export function clearRaiderApiCache() {
  requestCache.clear()
  clearPersistedCache()
  clearBrowserCache()
}

export async function getRaids(expansionId = 10, options = {}) {
  return request('/raiding/static-data', { expansion_id: expansionId }, {
    ttl: STATIC_DATA_TTL_MS,
    ...options,
  })
}

export async function getRaidRankings(params, options = {}) {
  return request('/raiding/raid-rankings', params, options)
}

export async function getRaidHallOfFame(params, options = {}) {
  return request('/raiding/hall-of-fame', params, options)
}

export async function getMythicPlusStaticData(expansionId = 10, options = {}) {
  return request('/mythic-plus/static-data', { expansion_id: expansionId }, {
    ttl: STATIC_DATA_TTL_MS,
    ...options,
  })
}

export async function getMythicPlusRuns(params, options = {}) {
  return request('/mythic-plus/runs', params, options)
}

export async function getAffixes(region = 'eu', locale = 'ru', options = {}) {
  return request('/mythic-plus/affixes', { region, locale }, options)
}

export async function getSeasonCutoffs(region = 'eu', season, options = {}) {
  return request('/mythic-plus/season-cutoffs', { region, season }, options)
}

export async function getLeaderboardCapacity(region = 'eu', realm = '', scope = 'current', options = {}) {
  return request('/mythic-plus/leaderboard-capacity', { region, realm, scope }, options)
}

export async function getCharacterProfile({ region, realm, name, fields }, options = {}) {
  return request('/characters/profile', {
    region,
    realm,
    name,
    fields,
  }, options)
}
